// Contenido y navegación por teclado del menú "+" de la barra de entrada.
//
// El ORDEN de este array es el orden que se ve. Está acá, en un módulo puro y
// sin JSX, por dos motivos: se testea (que el orden y las etiquetas son los
// pedidos) y el componente queda siendo solo dibujo + eventos.
//
// Dos bloques: de dónde sale lo que se adjunta (archivos propios + los catálogos de
// la marca — ADR-2026-09-08-adjuntar-desde-el-catalogo-de-la-marca), y las pantallas de
// adentro —Herramientas (los tres interruptores del chat, `INTERRUPTORES_MENU_MAS`,
// ADR-2026-09-18-herramientas-es-una-pantalla-del-menu-del-mas), Preferencias de
// contenido (ADR-2026-09-13-preferencias-de-generacion-en-el-menu-del-mas) y Opciones de
// modelo avanzadas (ADR-2026-09-14-el-modelo-de-imagen-lo-elige-el-usuario)— más
// Conectores (todavía no existe).
// "Habilidades" ya no está: las skills viven en su propia hoja del "⋯".
//
// Sin separadores: la grilla de cards ya es un bloque visual por sí sola y una raya
// debajo se leía como una línea de más (ADR-2026-09-13-el-menu-del-mas-con-titulo-y-cierre);
// el que apartaba a los interruptores se fue con ellos a su pantalla.
//
// La entrada `proximamente` (Conectores) SÍ se enfoca con las flechas aunque no se
// pueda activar. Es a propósito y sigue la práctica de "disabled focusable" de
// WAI-ARIA: si se la salteara, quien navega con teclado o lector de pantalla nunca se
// enteraría de que existe — que es justamente lo que el menú quiere comunicar.

import type { InterruptoresChat } from '../tipos';
import { palabraDeModeloActual } from '../../lib/nombreDeModelo';

/** Qué abre cada acción del menú. `archivos` es el `<input type=file>`; el resto son
 *  los selectores reales de la app (los mismos que usan los modos). */
export type AccionMenuMas =
  | 'archivos'
  | 'producto'
  | 'modelo'
  | 'pose'
  | 'fondo'
  | 'generada';

/** Las pantallas que viven adentro del menú. La lista, aparte, es `'principal'`. */
export type VistaDelMenu = 'herramientas' | 'preferencias' | 'modelos';

/** Un interruptor del chat: vive en la pantalla "Herramientas", no en la lista. */
export interface ItemInterruptor {
  tipo: 'toggle';
  id: keyof InterruptoresChat;
  etiqueta: string;
  icono: 'lupa-doc' | 'globo' | 'grafico';
  ayuda: string;
}

export type ItemMenuMas =
  | {
      tipo: 'accion';
      id: AccionMenuMas;
      etiqueta: string;
      icono: 'clip' | 'paquete' | 'persona' | 'pose' | 'imagen' | 'chispas';
      ayuda: string;
    }
  | { tipo: 'separador'; id: string }
  /** Abre una pantalla ADENTRO del menú (no cierra). El `id` ES la pantalla que abre:
   *  `MenuMas` lo usa de estado, así que sumar una vista es sumar una entrada acá. */
  | {
      tipo: 'vista';
      id: VistaDelMenu;
      etiqueta: string;
      icono: 'herramientas' | 'ajustes' | 'chip';
      ayuda: string;
    }
  | {
      tipo: 'proximamente';
      id: 'conectores';
      etiqueta: string;
      icono: 'enchufe';
      ayuda: string;
    }
  | ItemInterruptor;

export const ITEMS_MENU_MAS: ItemMenuMas[] = [
  {
    tipo: 'accion',
    id: 'archivos',
    etiqueta: 'Agregar archivos o fotos',
    // Muchas fotos de una (7 o más) entran como LOTE de productos por esta misma
    // entrada: cargar productos es una capacidad del agente, no una opción del menú
    // (ADR-2026-09-08-agregar-productos-es-una-herramienta).
    ayuda: 'Imágenes o archivos de texto',
    icono: 'clip',
  },
  {
    tipo: 'accion',
    id: 'producto',
    etiqueta: 'Producto',
    ayuda: 'Elegilo de tu catálogo',
    icono: 'paquete',
  },
  {
    tipo: 'accion',
    id: 'modelo',
  // "Modelo" en Moda, "Avatar" en el resto: `get` porque la tabla se arma una vez al
  // cargar el módulo y la marca se cambia sin recargar
  // (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo).
    get etiqueta() {
      return palabraDeModeloActual().titulo;
    },
    get ayuda() {
      return `Los ${palabraDeModeloActual().plural} guardados de tu marca`;
    },
    icono: 'persona',
  },
  {
    tipo: 'accion',
    id: 'pose',
    etiqueta: 'Pose',
    ayuda: 'La postura que querés que tenga',
    icono: 'pose',
  },
  {
    tipo: 'accion',
    id: 'fondo',
    etiqueta: 'Fondo',
    ayuda: 'Un escenario de la biblioteca o tuyo',
    icono: 'imagen',
  },
  {
    tipo: 'accion',
    id: 'generada',
    etiqueta: 'Imagen ya generada',
    ayuda: 'Algo que creaste antes, de tus creaciones',
    icono: 'chispas',
  },
  {
    tipo: 'vista',
    id: 'herramientas',
    etiqueta: 'Herramientas',
    icono: 'herramientas',
    ayuda: 'Investigación, búsqueda web y respuestas gráficas.',
  },
  {
    tipo: 'vista',
    id: 'preferencias',
    etiqueta: 'Preferencias de contenido',
    icono: 'ajustes',
    ayuda: 'Formato y calidad por defecto para imagen y video.',
  },
  {
    tipo: 'vista',
    id: 'modelos',
    etiqueta: 'Opciones de modelo avanzadas',
    icono: 'chip',
    // No nombra ningún motor: la lista está a un toque y ahí sí se ven. Y "avanzadas" en la
    // etiqueta ya avisa que el default está bien para casi todo el mundo.
    ayuda: 'Elegí con qué motor de IA se generan tus imágenes.',
  },
  {
    tipo: 'proximamente',
    id: 'conectores',
    etiqueta: 'Conectores',
    icono: 'enchufe',
    ayuda: 'Vas a poder enchufarle tus herramientas (Drive, Notion, Tiendanube…).',
  },
];

/** Los interruptores del chat, en el orden de la pantalla "Herramientas". El estado
 *  es el de siempre (`InterruptoresChat`); acá sólo cambió dónde se prenden. */
export const INTERRUPTORES_MENU_MAS: ItemInterruptor[] = [
  {
    tipo: 'toggle',
    id: 'investigacion',
    etiqueta: 'Investigación',
    icono: 'lupa-doc',
    ayuda: 'Mira datos reales de Instagram, Google Maps y tiendas online.',
  },
  {
    tipo: 'toggle',
    id: 'busquedaWeb',
    etiqueta: 'Búsqueda web',
    icono: 'globo',
    ayuda: 'Busca en internet antes de responder.',
  },
  {
    tipo: 'toggle',
    id: 'respuestasGraficas',
    etiqueta: 'Respuestas gráficas',
    icono: 'grafico',
    ayuda: 'Puede contestar con un gráfico o una tabla cuando ayuda.',
  },
];

/** Rótulo de las entradas todavía no construidas. */
export const ROTULO_PROXIMAMENTE = 'próximamente';

/** ¿Se puede ACTIVAR con Enter/click? Los separadores no existen para el
 *  teclado y las entradas "próximamente" se enfocan pero no hacen nada. */
export const esActivable = (item: ItemMenuMas): boolean =>
  item.tipo === 'accion' || item.tipo === 'toggle' || item.tipo === 'vista';

/** ¿Recibe foco con las flechas? Todo menos los separadores. */
export const esEnfocable = (item: ItemMenuMas): boolean => item.tipo !== 'separador';

/** Los ids que recorre el foco, en orden. */
export const idsEnfocables = (items: ItemMenuMas[] = ITEMS_MENU_MAS): string[] =>
  items.filter(esEnfocable).map((i) => i.id);

/**
 * A qué id se mueve el foco desde `actual` corriéndose `delta` lugares.
 * Circular en los dos sentidos (el último baja al primero). Si `actual` no
 * está en la lista, se arranca por el extremo que corresponda al sentido.
 */
export function idFocoSiguiente(
  actual: string | null,
  delta: 1 | -1,
  items: ItemMenuMas[] = ITEMS_MENU_MAS,
): string | null {
  const ids = idsEnfocables(items);
  if (ids.length === 0) return null;
  const posicion = actual === null ? -1 : ids.indexOf(actual);
  if (posicion === -1) return delta === 1 ? ids[0] : ids[ids.length - 1];
  const siguiente = (posicion + delta + ids.length) % ids.length;
  return ids[siguiente];
}

/** Primer / último id enfocable (Home y End). */
export const idPrimerFoco = (items: ItemMenuMas[] = ITEMS_MENU_MAS): string | null =>
  idsEnfocables(items)[0] ?? null;

export const idUltimoFoco = (items: ItemMenuMas[] = ITEMS_MENU_MAS): string | null => {
  const ids = idsEnfocables(items);
  return ids[ids.length - 1] ?? null;
};
